import { useEffect, useState } from "react";
import { Check, Mic, Plus, Settings2, Square } from "lucide-react";
import { toast } from "sonner";
import { api, errorText } from "@/lib/api";
import { useRecorder } from "./Chat";
import StrategyRoomCTA from "@/components/workspace/StrategyRoomCTA";
import { groupMemory, profileKind } from "@/lib/businessIntegrity";

export default function Intake({ lang, t, data, setData }) {
  const fa = lang === "fa";
  const [context, setContext] = useState(null);
  const [message, setMessage] = useState("");
  const [reply, setReply] = useState("");
  const [sending, setSending] = useState(false);
  const [creating, setCreating] = useState(false);
  const [newBusiness, setNewBusiness] = useState({ business_name: "", industry: "" });
  const [profileName, setProfileName] = useState(data.user?.name || "");
  const [savingProfile, setSavingProfile] = useState(false);
  const loadContext = async () => {
    const result = await api.get("/intake/context");
    setContext(result.data);
  };

  useEffect(() => { loadContext().catch(() => toast.error(fa ? "اطلاعات نورین در دسترس نیست." : "NOORIN information is unavailable.")); }, [fa]);

  const { recording, transcribing, start, stop } = useRecorder({
    t,
    onText: (text) => setMessage((value) => value ? `${value} ${text}` : text),
  });

  const record = async (event) => {
    event.preventDefault();
    const text = message.trim();
    if (!text || sending || !context) return;
    setSending(true);
    setReply("");
    try {
      const result = await api.post("/intake/messages", { message: text, business_id: context.business.business_id });
      setReply(result.data.message);
      if (result.data.recorded) {
        setMessage("");
        setContext((current) => ({ ...current, memory: [result.data.item, ...(current.memory || [])], timeline: [result.data.event, ...(current.timeline || [])] }));
        setData((current) => ({ ...current, business: { ...current.business, sample_data: false }, businesses: (current.businesses || []).map((business) => business.business_id === context.business.business_id ? { ...business, sample_data: false } : business) }));
      }
    } catch (err) { toast.error(errorText(err, fa ? "ثبت اطلاعات انجام نشد." : "The information was not recorded.")); }
    finally { setSending(false); }
  };

  const selectBusiness = async (businessId) => {
    try {
      await api.post(`/businesses/${businessId}/activate`);
      const selected = (data.businesses || []).find((business) => business.business_id === businessId);
      setData((current) => ({ ...current, business: selected || current.business }));
      setReply("");
      await loadContext();
    } catch (err) { toast.error(errorText(err, fa ? "تغییر کسب‌وکار انجام نشد." : "Could not change business.")); }
  };

  const addBusiness = async (event) => {
    event.preventDefault();
    if (!newBusiness.business_name.trim()) return;
    try {
      const result = await api.post("/businesses", newBusiness);
      setData((current) => ({ ...current, business: result.data, businesses: [result.data, ...(current.businesses || [])] }));
      setNewBusiness({ business_name: "", industry: "" });
      setCreating(false);
      setReply("");
      await loadContext();
      toast.success(fa ? "کسب‌وکار جدید آماده است." : "Your new business is ready.");
    } catch (err) { toast.error(errorText(err, fa ? "ساخت کسب‌وکار انجام نشد." : "Could not create the business.")); }
  };

  const saveProfile = async (event) => {
    event.preventDefault();
    setSavingProfile(true);
    try {
      const result = await api.patch("/account", { name: profileName, preferred_language: lang });
      setData((current) => ({ ...current, user: result.data }));
      toast.success(t.saved);
    } catch (err) { toast.error(errorText(err, fa ? "تنظیمات ذخیره نشد." : "Settings could not be saved.")); }
    finally { setSavingProfile(false); }
  };

  if (!context) return <div className="loading">{t.loading}</div>;
  const kind = profileKind(context.business);
  const memoryGroups = groupMemory(context.memory);

  return (
    <section className="intake-page section-page">
      <div className="strategy-invite"><div><h2>{t.startConversation}</h2><p>{t.startConversationHint}</p></div><StrategyRoomCTA t={t} prominent testId="intake-strategy-room" /></div>
      <div className="page-title-row">
        <div><p className="eyebrow">{t.eyebrows.intake}</p><h2>{fa ? "ثبت اطلاعات کسب‌وکار" : "Record business information"}</h2><p className="muted">{fa ? "هرطور راحت‌ترید بنویسید؛ نورین اطلاعات روشن را ثبت می‌کند و فقط برای موارد مبهم یک سؤال کوتاه می‌پرسد. مثال‌های فرضی به‌عنوان واقعیت تأییدشده ذخیره نمی‌شوند." : "Write naturally. NOORIN records clear information and asks one short question only when something is unclear. Hypothetical examples are not stored as confirmed facts."}</p></div>
        <details className="intake-settings"><summary><Settings2 size={16} />{fa ? "تنظیمات حساب" : "Account settings"}</summary><form className="stack-form" onSubmit={saveProfile}><label>{fa ? "نام نمایشی" : "Display name"}<input value={profileName} onChange={(event) => setProfileName(event.target.value)} required /></label><button className="quiet-button" disabled={savingProfile}>{t.save}</button></form></details>
      </div>

      <div className="intake-business-bar">
        <label>{fa ? "کسب‌وکار فعال" : "Active business"}<select value={context.business.business_id} onChange={(event) => selectBusiness(event.target.value)}>{(data.businesses || [context.business]).map((business) => <option key={business.business_id} value={business.business_id}>{business.business_name}</option>)}</select></label>
        <button className="quiet-button" type="button" onClick={() => setCreating((value) => !value)}><Plus size={16} />{fa ? "کسب‌وکار جدید" : "New business"}</button>
      </div>
      {creating && <form className="card-form intake-new-business" onSubmit={addBusiness}><label>{t.businessName}<input value={newBusiness.business_name} onChange={(event) => setNewBusiness({ ...newBusiness, business_name: event.target.value })} required /></label><label>{t.industry}<input value={newBusiness.industry} onChange={(event) => setNewBusiness({ ...newBusiness, industry: event.target.value })} /></label><div className="row-actions"><button className="primary-button">{fa ? "ساخت و انتخاب" : "Create and select"}</button><button className="quiet-button" type="button" onClick={() => setCreating(false)}>{t.cancel}</button></div></form>}

      <div className="intake-grid">
        <div className="intake-record">
          <div className="intake-current"><span className="verified-dot" /><div><strong>{context.business.business_name}</strong><p>{kind === "demonstration" ? t.demoProfile : (context.business.industry || t.noIndustry)}</p>{kind === "demonstration" && <p>{t.demoProfileNote}</p>}</div></div>
          <form className="chat-composer intake-composer" onSubmit={record}><textarea value={message} onChange={(event) => setMessage(event.target.value)} placeholder={transcribing ? t.transcribing : (fa ? "مثلاً: ماه قبل ۱۳ سفارش داشتیم و بیشتر مشتری‌ها فقط قیمت می‌پرسند." : "For example: We had 13 orders last month and most customers only ask for prices.")} onKeyDown={(event) => { if (event.key === "Enter" && !event.shiftKey) record(event); }} /><div className="composer-actions"><button type="button" className={`mic-button ${recording ? "recording" : ""}`} onClick={recording ? stop : start} disabled={transcribing} title={recording ? t.stop : t.record}>{recording ? <Square size={16} /> : <Mic size={17} />}</button><button className="primary-button" disabled={sending || !message.trim()}>{sending ? "…" : (fa ? "ثبت" : "Record")}<Check size={17} /></button></div></form>
          {reply && <p className="intake-reply" data-testid="intake-reply">{reply}</p>}
        </div>
        <aside className="intake-memory" data-testid="intake-memory"><p className="eyebrow">{t.memoryTitle}</p><h3>{fa ? "آخرین اطلاعات ثبت‌شده" : "Recent records"}</h3><p className="source-note">{t.memoryIntro}</p>{memoryGroups.length ? memoryGroups.map((group) => <div className="memory-group" key={group.kind} data-testid={`memory-group-${group.kind}`}><h4>{t.memoryGroups[group.kind]}</h4><ul>{group.items.slice(0, 5).map((item, index) => <li key={`${item.created_at}-${index}`}>{item.content}<small>{item.created_at ? new Date(item.created_at).toLocaleString(fa ? "fa-IR" : "en-GB") : ""}</small></li>)}</ul></div>) : <p className="muted">{t.memoryEmpty}</p>}</aside>
      </div>
      <div className="intake-timeline"><p className="eyebrow">{t.eyebrows.timeline}</p><h3>{t.timelineTitle}</h3><p className="source-note">{t.timelineSource}</p>{context.timeline?.length ? <div className="list">{context.timeline.slice(0, 8).map((event, index) => <div className="list-item" key={`${event.created_at}-${index}`}><strong>{event.summary}</strong><small>{new Date(event.created_at).toLocaleString(fa ? "fa-IR" : "en-GB")}{event.source ? ` · ${event.source}` : ""}</small></div>)}</div> : <p className="muted" data-testid="timeline-empty">{t.timelineEmpty}</p>}</div>
    </section>
  );
}
